import React, { useEffect, useState, useMemo } from 'react';
import axiosInstance from '../axiosInstance';
import './Monitoring.css';

const Monitoring = () => {
  const [properties, setProperties] = useState([]);
  const [floors, setFloors] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State to track the currently selected object
  const [selectedObject, setSelectedObject] = useState(null); // { type: 'company' | 'property' | 'floor' | 'table', id: string }

  /**
   * Handle icon clicks to set the selected object.
   * If the same object is clicked again, deselect it.
   * @param {string} type - The type of the object ('company', 'property', 'floor', 'table').
   * @param {string} id - The unique identifier of the object.
   */
  const handleIconClick = (type, id) => {
    console.log(`Clicked on ${type}: ${id}`);
    if (selectedObject && selectedObject.type === type && selectedObject.id === id) {
      // Deselect if the same object is clicked
      setSelectedObject(null);
    } else {
      setSelectedObject({ type, id });
    }
  };

  /**
   * Fetch all necessary data (properties, floors, tables, and table status) associated with the authenticated customer.
   */
  const fetchAllData = async () => {
    try {
      // 1. Fetch properties
      const propertiesResponse = await axiosInstance.get('/api/properties');
      console.log('PROPERTIES response data:', propertiesResponse.data);
      setProperties(propertiesResponse.data);

      // 2. Fetch floors
      const floorsResponse = await axiosInstance.get('/api/allFloors');
      console.log('ALL FLOORS response data:', floorsResponse.data);
      setFloors(floorsResponse.data);

      // 3. Fetch table statuses (including table details)
      const tableStatusResponse = await axiosInstance.get('/api/tableStatus');
      console.log('TABLE STATUS response data:', tableStatusResponse.data);
      setTables(tableStatusResponse.data); // Assuming tableStatus includes all table details

      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
      setLoading(false);
    }
  };

  /**
   * Set up polling for table status every minute once initial load is complete.
   */
  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (!loading) {
      const intervalId = setInterval(() => {
        fetchAllData(); // Re-fetch all data periodically
      }, 60000); // 1 minute

      return () => clearInterval(intervalId);
    }
  }, [loading]);

  /**
   * Create a map for quick lookup of table statuses.
   * @returns {object} - Mapping of table_id to is_online status.
   */
  const tableStatusMap = useMemo(() => {
    const map = {};
    tables.forEach((t) => {
      map[t.table_id] = t.is_online;
    });
    return map;
  }, [tables]);

  /**
   * Compute property statuses based on their associated tables' statuses.
   * @returns {object} - Mapping of property_id to status ('online', 'offline', 'amber', 'no-tables')
   */
  const propertyStatuses = useMemo(() => {
    const statusMap = {};

    properties.forEach((property) => {
      // Find all tables for this property
      const propertyTables = tables.filter((t) => t.property_id === property.property_id);

      if (propertyTables.length === 0) {
        statusMap[property.property_id] = 'no-tables';
        return;
      }

      // Determine statuses of tables using tableStatusMap
      const onlineTables = propertyTables.filter((t) => tableStatusMap[t.table_id]).length;
      const totalTables = propertyTables.length;

      if (onlineTables === totalTables) {
        statusMap[property.property_id] = 'online';
      } else if (onlineTables === 0) {
        statusMap[property.property_id] = 'offline';
      } else {
        statusMap[property.property_id] = 'amber';
      }
    });

    return statusMap;
  }, [properties, tables, tableStatusMap]);

  /**
   * Compute company status based on all properties' statuses.
   * - All 'online' => 'online'
   * - All 'offline' => 'offline'
   * - Otherwise => 'amber'
   */
  const companyStatus = useMemo(() => {
    if (properties.length === 0) {
      return 'no-tables'; // Optionally handle no properties scenario
    }

    const statuses = properties.map((property) => propertyStatuses[property.property_id]);

    // If any property has 'no-tables', treat the company status as 'amber'
    if (statuses.includes('no-tables')) {
      return 'amber';
    }

    const allOnline = statuses.every((status) => status === 'online');
    const allOffline = statuses.every((status) => status === 'offline');

    if (allOnline) return 'online';
    if (allOffline) return 'offline';
    return 'amber';
  }, [properties, propertyStatuses]);

  /**
   * Determine which properties to display based on the current selection.
   */
  const visibleProperties = useMemo(() => {
    if (!selectedObject) {
      return properties;
    }

    switch (selectedObject.type) {
      case 'company':
        return properties;
      case 'property':
        return properties.filter((p) => p.property_id === selectedObject.id);
      case 'floor':
        // Find the floor and its associated property
        const floor = floors.find((f) => f.floor_id === selectedObject.id);
        if (floor) {
          return properties.filter((p) => p.property_id === floor.property_id);
        }
        return [];
      case 'table':
        // Find the table and its associated floor
        const table = tables.find((t) => t.table_id === selectedObject.id);
        if (table) {
          return properties.filter((p) => p.property_id === table.property_id);
        }
        return [];
      default:
        return properties;
    }
  }, [selectedObject, properties, floors, tables]);

  /**
   * Determine which floors to display based on the current selection.
   */
  const visibleFloors = useMemo(() => {
    if (!selectedObject) {
      return floors; // no object selected => show all
    }
  
    switch (selectedObject.type) {
      case 'company':
        return floors; // all floors visible
      case 'property':
        return floors.filter((f) => f.property_id === selectedObject.id);
      case 'floor': {
        const floor = floors.find((f) => f.floor_id === selectedObject.id);
        if (!floor) return [];
        // Show **all floors** in the same property, not just the selected floor
        return floors.filter((f) => f.property_id === floor.property_id);
      }
      case 'table': {
        const table = tables.find((t) => t.table_id === selectedObject.id);
        if (!table) return [];
        // Show all floors in the same property as this table
        const theFloor = floors.find((f) => f.floor_id === table.floor_id);
        if (!theFloor) return [];
        return floors.filter((f) => f.property_id === theFloor.property_id);
      }
      default:
        return floors;
    }
  }, [selectedObject, floors, tables]);

  /**
   * Determine which tables to display based on the current selection.
   */
  const visibleTables = useMemo(() => {
    if (!selectedObject) {
      return tables;
    }

    switch (selectedObject.type) {
      case 'company':
        return tables;
      case 'property':
        return tables.filter((t) => t.property_id === selectedObject.id);
      case 'floor':
        return tables.filter((t) => t.floor_id === selectedObject.id);
      case 'table':
        return tables.filter((t) => t.table_id === selectedObject.id);
      default:
        return tables;
    }
  }, [selectedObject, tables]);

  /**
   * Debugging: Log visible entities whenever selection changes
   */
  useEffect(() => {
    console.log('Selected Object:', selectedObject);
    console.log('Visible Properties:', visibleProperties);
    console.log('Visible Floors:', visibleFloors);
    console.log('Visible Tables:', visibleTables);
  }, [selectedObject, visibleProperties, visibleFloors, visibleTables]);

  /**
   * Render loading or error states.
   */
  if (loading) {
    return <div className="monitoring-container">Loading data...</div>;
  }

  if (error) {
    return <div className="monitoring-container">{error}</div>;
  }

  return (
    <div className="monitoring-container">
      {/* Top Section: Logo and Company Icon */}
      <div className="monitoring-section top-section">

        {/* Company Icon */}
        <img
          src="/monitoring/logo.png" // Using logo.png as the company icon
          alt="Company"
          className={`monitoring-icon company-icon ${companyStatus} ${selectedObject && selectedObject.type === 'company' ? 'selected' : ''}`}
          onClick={() => handleIconClick('company', 'company')} // 'company' id is arbitrary since there's only one
          title={`Company Status: ${companyStatus.charAt(0).toUpperCase() + companyStatus.slice(1)}`}
        />
      </div>

      {/* Middle Section: Properties */}
      <div className="monitoring-section middle-section">
        {visibleProperties.map((property) => (
          <img
            key={property.property_id}
            src="/monitoring/property.png"
            alt={property.property_name}
            className={`monitoring-icon ${propertyStatuses[property.property_id]} ${selectedObject && selectedObject.type === 'property' && selectedObject.id === property.property_id ? 'selected' : ''}`}
            onClick={() => handleIconClick('property', property.property_id)}
            title={`Property: ${property.property_name} (${propertyStatuses[property.property_id].charAt(0).toUpperCase() + propertyStatuses[property.property_id].slice(1)})`}
          />
        ))}
      </div>

      {/* Floors Section */}
      <div className="monitoring-section floors-section">
        {visibleFloors.map((floor) => {
          // Find all tables for this floor within visibleTables
          const floorTables = visibleTables.filter((t) => t.floor_id === floor.floor_id);

          if (floorTables.length === 0) {
            return (
              <img
                key={floor.floor_id}
                src="/monitoring/floors.png"
                alt={`${floor.floor_type} - ${floor.floor_name}`}
                className={`monitoring-icon no-tables ${selectedObject && selectedObject.type === 'floor' && selectedObject.id === floor.floor_id ? 'selected' : ''}`}
                onClick={() => handleIconClick('floor', floor.floor_id)}
                title={`${floor.floor_type} - ${floor.floor_name} (No Tables)`}
              />
            );
          }

          // Determine floor status using tableStatusMap
          const onlineTables = floorTables.filter((t) => tableStatusMap[t.table_id]).length;
          const totalTables = floorTables.length;
          let floorStatus = 'amber'; // Default

          if (onlineTables === totalTables) {
            floorStatus = 'online';
          } else if (onlineTables === 0) {
            floorStatus = 'offline';
          }

          return (
            <img
              key={floor.floor_id}
              src="/monitoring/floors.png"
              alt={`${floor.floor_type} - ${floor.floor_name}`}
              className={`monitoring-icon ${floorStatus} ${selectedObject && selectedObject.type === 'floor' && selectedObject.id === floor.floor_id ? 'selected' : ''}`}
              onClick={() => handleIconClick('floor', floor.floor_id)}
              title={`${floor.floor_type} - ${floor.floor_name} (${floorStatus.charAt(0).toUpperCase() + floorStatus.slice(1)})`}
            />
          );
        })}
      </div>

      {/* Tables Section */}
      <div className="monitoring-section tables-section">
        {visibleTables.map((table) => {
          const online = tableStatusMap[table.table_id];
          return (
            <div key={table.table_id} className="table-item">
              <img
                src="/monitoring/table.png"
                alt={table.table_name}
                className={`monitoring-icon ${online ? 'online' : 'offline'} ${selectedObject && selectedObject.type === 'table' && selectedObject.id === table.table_id ? 'selected' : ''}`}
                onClick={() => handleIconClick('table', table.table_id)}
                title={`${table.table_name} - (${online ? 'Online' : 'Offline'})`}
              />
              <div className="table-label">
                {table.table_name} ({online ? 'Online' : 'Offline'})
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Monitoring;
